import React from 'react'
import * as PropTypes from 'prop-types'
import { TextField, InputAdornment, CircularProgress } from '@material-ui/core'
import withStyles from '@material-ui/core/styles/withStyles'
import i18n from 'support/i18n'
import TextEditor from 'components/text_editor/View'
import ImageUploader from 'components/image_uploader/View'
import NxtDatePicker from 'components/nxt_date_picker/View'
import VideoForm from 'components/video_form/View'
import styles from './View.Style'

const MainFormSection = props => {
  const {
    classes,
    titleValue,
    imageValues,
    descriptionValue,
    externalVideoValue,
    fileVideoSrc,
    fileVideoType,
    onExternalVideoChange,
    onSelectFileVideo,
    onRemoveFileVideo,
    isFileVideoLoading,
    isFileVideoReady,
    isVideoRemoving,
    expireDateValue,
    minDate,
    titleError,
    imageError,
    onTitleChange,
    descriptionError,
    onDescriptionChange,
    onChangeExpiredDate,
    titleRef,
    descriptionRef,
    imageRef,
    isTitleLoading,
    onSelectImage,
    deleteImage,
    missingImageError,
    externalVideoRef,
    hasVideoError,
    onExternalVideoError,
    disableUploadVideoFile,
    disableExpireDate,
    customIdeaTitle,
  } = props

  return (
    <div>
      <div className={classes.formItem}>
        <h3>{i18n.t('IDEA.TITLE', { title: customIdeaTitle || 'idea' })}</h3>
        <div className={classes.inputContainer} ref={titleRef}>
          <TextField
            className={classes.inputItem}
            placeholder={i18n.t('IDEA.TITLE', { title: customIdeaTitle || 'idea' })}
            value={titleValue}
            margin="normal"
            fullWidth
            InputProps={{
              endAdornment: isTitleLoading ? (
                <InputAdornment position="start">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : null,
            }}
            onChange={event => {
              onTitleChange(event.target.value)
            }}
            error={!!titleError}
          />
          <span>{titleError}</span>
        </div>
      </div>
      <div className={classes.formItem}>
        <h3>{i18n.t('IDEA.IDEA_IMAGES', { title: customIdeaTitle || 'idea' })}</h3>
        <div className={classes.imageContainer} ref={imageRef}>
          <ImageUploader
            images={imageValues}
            imageUploadError={imageError}
            onSelectImage={onSelectImage}
            deleteImage={deleteImage}
          />
          <span>{missingImageError}</span>
        </div>
      </div>
      <div className={classes.formItem}>
        <h3>{i18n.t('IDEA.DESC', { title: customIdeaTitle || 'idea' })}</h3>
        <div className={classes.inputContainer}>
          <TextEditor
            classes={{ description: classes.inputItem }}
            reference={descriptionRef}
            onTextChange={onDescriptionChange}
            value={descriptionValue}
          />
          <span>{descriptionError}</span>
        </div>
      </div>
      <div className={classes.formItem} ref={externalVideoRef}>
        <h3>{i18n.t('IDEA.VIDEO', { title: customIdeaTitle || 'idea' })}</h3>
        <div className={classes.inputItem}>
          <VideoForm
            onExternalVideoError={onExternalVideoError}
            externalVideoSrc={externalVideoValue}
            fileVideoSrc={fileVideoSrc}
            fileVideoType={fileVideoType}
            onChangeExternalVideo={onExternalVideoChange}
            onSelectFileVideo={onSelectFileVideo}
            onRemoveFileVideo={onRemoveFileVideo}
            isVideoLoading={isFileVideoLoading}
            isVideoReady={isFileVideoReady}
            isVideoRemoving={isVideoRemoving}
            hasVideoError={hasVideoError}
            disableUploadVideoFile={disableUploadVideoFile}
          />
        </div>
      </div>
      {!disableExpireDate && expireDateValue !== undefined && (
        <div className={classes.formItem}>
          <h3>{i18n.t('IDEA.WHEN_ACTIVE')}</h3>
          <NxtDatePicker
            className={classes.dateInputItem}
            id="date-picker-from"
            onDateChange={onChangeExpiredDate}
            value={expireDateValue}
            endIconName="icon-th"
            minDate={minDate}
          />
        </div>
      )}
    </div>
  )
}

MainFormSection.defaultProps = {
  titleValue: '',
  descriptionValue: '',
  externalVideoValue: '',
  // eslint-disable-next-line react/default-props-match-prop-types
  fileVideo: {
    blobKey: '',
    bucket: '',
    created: '',
    id: 0,
    key: '',
    name: '',
    size: 0,
    type: '',
  },
  fileVideoSrc: '',
  fileVideoType: '',
  expireDateValue: undefined,
  minDate: new Date(),
  titleRef: null,
  imageRef: null,
  descriptionRef: null,
  externalVideoRef: null,
  titleError: '',
  descriptionError: '',
  missingImageError: '',
  isTitleLoading: false,
  hasVideoError: false,
  disableUploadVideoFile: false,
  customIdeaTitle: '',
}

MainFormSection.propTypes = {
  classes: PropTypes.shape({
    icon: PropTypes.string,
    title: PropTypes.string,
    formItem: PropTypes.string,
    inputItem: PropTypes.string,
    inputContainer: PropTypes.string,
  }).isRequired,
  titleValue: PropTypes.string,
  descriptionValue: PropTypes.string,
  externalVideoValue: PropTypes.string,
  onExternalVideoChange: PropTypes.func.isRequired,
  fileVideoType: PropTypes.string,
  fileVideoSrc: PropTypes.string,
  onSelectFileVideo: PropTypes.func.isRequired,
  onRemoveFileVideo: PropTypes.func.isRequired,
  isFileVideoReady: PropTypes.bool.isRequired,
  isVideoRemoving: PropTypes.bool.isRequired,
  expireDateValue: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  imageValues: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      src: PropTypes.string,
      name: PropTypes.string,
    }).isRequired,
  ).isRequired,
  onTitleChange: PropTypes.func.isRequired,
  onDescriptionChange: PropTypes.func.isRequired,
  onChangeExpiredDate: PropTypes.func.isRequired,
  titleRef: PropTypes.shape({}),
  descriptionRef: PropTypes.shape({}),
  imageRef: PropTypes.shape({}),
  missingImageError: PropTypes.string,
  externalVideoRef: PropTypes.shape({}),
  titleError: PropTypes.string,
  descriptionError: PropTypes.string,
  isTitleLoading: PropTypes.bool,
  hasVideoError: PropTypes.bool,
  disableUploadVideoFile: PropTypes.bool,
  disableExpireDate: PropTypes.bool.isRequired,
  customIdeaTitle: PropTypes.string,
}

export default withStyles(styles)(MainFormSection)
